import { InvoiceStatus } from '@cyberlabrs/helpers';
import {
    AddressDto,
    ClientDto,
    ClientPublicDto,
    CreateClientDto,
    IClientFormAddress,
    PostInvoiceDto,
    PostTermsDto,
    TermDto,
    UpdateClientDto,
} from '../commonTypes/client.types';
import { InvoiceDto } from '../commonTypes/invoice.types';
import { diContainer } from '../libs/container';
import { ApiBaseClass, IServiceHttp } from './ApiBase';
import { AppConfig } from '../config/AppConfig';

export namespace ClientApiRequest {
    export interface GetAll {}

    export interface Create {
        body: CreateClientDto;
    }

    export interface Update {
        params: {
            clientId: string;
        };
        body: UpdateClientDto;
    }

    export interface Delete {
        params: {
            id: string;
        };
    }

    export interface CreateAddress {
        params: {
            clientId: string;
        };
        body: {
            addressType: 'billingAddress' | 'shippingAddress';
            address: IClientFormAddress;
        };
    }

    export interface UpdateAddress {
        params: {
            clientId: string;
        };
        body: {
            addressType: 'billingAddress' | 'shippingAddress';
            address: AddressDto;
        };
    }

    export interface DeleteAddress {
        params: {
            clientId: string;
            addressType: 'billingAddress' | 'shippingAddress';
        };
    }

    export interface CreateCustomField {
        params: {
            clientId: string;
        };
        body: {
            label: string;
            value: string;
        };
    }

    export interface GetOrganizationClientsCount {
        query: {
            from: string;
            to: string;
        };
    }

    export interface GetInvoicesByOrganization {
        query?: {
            clientId?: string;
            status?: string[];
            page?: number;
            size?: number;
            sort?: string;
            direction?: 'asc' | 'desc';
            search?: string;
        };
    }

    export interface PostTerms {
        body: PostTermsDto[];
    }

    export interface PostInvoice {
        body: PostInvoiceDto;
    }

    export interface SendInvoice {
        params: {
            id: string;
        };
    }

    export interface GetInvoiceById {
        params: {
            id: string;
        };
    }

    export interface PostTerms {
        body: PostTermsDto[];
    }

    export interface PostInvoice {
        body: PostInvoiceDto;
    }

    export interface PatchInvoice {
        params: {
            id: string;
        };
        body: Partial<PostInvoiceDto>;
    }

    export interface GetInvoiceById {
        params: {
            id: string;
        };
    }

    export interface ChangeStatus {
        params: {
            id: string;
            status: InvoiceStatus;
        };
    }
}

export namespace ClientApiResponse {
    export interface Create {
        body: ClientDto;
    }
    export interface GetAll {
        body: ClientDto[];
    }

    export interface GetAllForProject {
        body: ClientPublicDto[];
    }

    export interface CreateAddress {
        body: AddressDto;
    }

    export interface UpdateAddress {
        body: AddressDto;
    }
    export interface GetOrganizationClientsCount {
        body: {
            countClients: number;
            createdAtCount: number;
        };
    }

    export interface GetInvoicesByOrganization {
        body: {
            items: InvoiceDto[];
            total: number;
            page: number;
            size: number;
            hasNext: boolean;
        };
    }

    export interface GetTerms {
        body: TermDto[];
    }

    export interface PostInvoice {
        body: {};
    }

    export interface PatchInvoice {
        body: {};
    }

    export interface GetInvoiceById {
        body: InvoiceDto;
    }

    export interface SendInvoice {
        body: {};
    }
}

export class ClientApi {
    constructor(private readonly apiBase: ApiBaseClass) {}

    public getAll(rest: IServiceHttp<ClientApiRequest.GetAll, ClientApiResponse.GetAll>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'cli/v1/client.byOrganization',
        });
    }

    public getAllForProject(rest: IServiceHttp<ClientApiRequest.GetAll, ClientApiResponse.GetAllForProject>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'cli/v1/client.byOrganizationForProjectCreation',
        });
    }

    public create(rest: IServiceHttp<ClientApiRequest.Create, ClientApiResponse.Create>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'cli/v1/client',
        });
    }

    public update(rest: IServiceHttp<ClientApiRequest.Update, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'cli/v1/client/:clientId',
        });
    }

    public delete(rest: IServiceHttp<ClientApiRequest.Delete, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'cli/v1/client/:id',
        });
    }

    public createAddress(rest: IServiceHttp<ClientApiRequest.CreateAddress, ClientApiResponse.CreateAddress>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'cli/v1/address/:clientId',
        });
    }

    public updateAddress(rest: IServiceHttp<ClientApiRequest.UpdateAddress, ClientApiResponse.UpdateAddress>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'cli/v1/address/:clientId',
        });
    }

    public deleteAddress(rest: IServiceHttp<ClientApiRequest.DeleteAddress, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'cli/v1/address/:clientId/:addressType',
        });
    }

    public postCustomField(rest: IServiceHttp<ClientApiRequest.CreateCustomField, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'cli/v1/custom-fields/:clientId',
        });
    }

    public getOrganizationClientsCount(
        rest: IServiceHttp<ClientApiRequest.GetOrganizationClientsCount, ClientApiResponse.GetOrganizationClientsCount>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'cli/v1/client/organization-clients-count',
        });
    }

    public getInvoices(rest: IServiceHttp<ClientApiRequest.GetInvoicesByOrganization, ClientApiResponse.GetInvoicesByOrganization>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'cli/v1/invoice',
        });
    }

    public getTerms(rest: IServiceHttp<ClientApiRequest.GetAll, ClientApiResponse.GetTerms>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'cli/v1/terms',
        });
    }

    public postTerms(rest: IServiceHttp<ClientApiRequest.PostTerms, ClientApiResponse.GetTerms>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'cli/v1/terms',
        });
    }

    public postInvoice(rest: IServiceHttp<ClientApiRequest.PostInvoice, ClientApiResponse.PostInvoice>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'cli/v1/invoice',
        });
    }

    public patchInvoice(rest: IServiceHttp<ClientApiRequest.PatchInvoice, ClientApiResponse.PatchInvoice>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'cli/v1/invoice/id/:id',
        });
    }

    public getInvoiceById(rest: IServiceHttp<ClientApiRequest.GetInvoiceById, ClientApiResponse.GetInvoiceById>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'cli/v1/invoice/id/:id',
        });
    }
    public deleteInvoice(rest: IServiceHttp<ClientApiRequest.Delete, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'cli/v1/invoice/id/:id',
        });
    }
    public changeStatus(rest: IServiceHttp<ClientApiRequest.ChangeStatus, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'cli/v1/invoice/id/:id/status/:status/-changeStatus',
        });
    }

    public async uploadInvoiceFile(rest: { id: string; file: File; accessToken?: string; idToken?: string }): Promise<{ body: any }> {
        const headers = new Headers();
        if (rest.idToken) headers.append('X-Id-Token', rest.idToken);
        if (rest.accessToken) headers.append('Authorization', `Bearer ${rest.accessToken}`);

        const formData = new FormData();
        formData.append('file', rest.file);

        const res = await fetch(`${AppConfig.serverUrlBase}/cli/v1/invoice/id/${rest.id}/upload`, {
            method: 'PUT',
            headers,
            body: formData,
        });

        if (!res.ok) {
            throw new Error(`Failed to upload invoice file (status: ${res.status})`);
        }

        return {
            body: await res.json(),
        };
    }

    public sendInvoice(rest: IServiceHttp<ClientApiRequest.SendInvoice, ClientApiResponse.SendInvoice>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: `cli/v1/invoice/id/:id/-send`,
        });
    }
}

diContainer.bindAsDynamic(ClientApi, c => new ClientApi(c.get(ApiBaseClass)));
